import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import { MenuNodeSlice, EditMenuNodeSlice, EditRebootDetail } from './types';
import { cloneDeep } from 'lodash';
import { getNodes } from '../../api/nodes';
import { setFeatures, putNodeRebootDetails} from '../../api/positions';
import { setTemporaryMessage } from '../rules/settingsSlice';
import { loadMonitor } from './monitorsSlice';
import { batch } from 'react-redux';
import { notUndefined, notNull, isNull } from '../../lib/typeCheck';
import { instrument_types, instrument_sub_types } from '../../components/common/Types';
import moment from 'moment';
// https://daveceddia.com/access-redux-store-outside-react/

const initialState: Array<MenuNodeSlice> = [];

let nextEventId = 4;

export const menuNodesSlice = createSlice({
  name: 'menuNodes',
  initialState,
  reducers: {
    loadMenuNodes: (state, action: PayloadAction<Array<MenuNodeSlice>>) => {
      return action.payload;
    },    
    getNodesFailed: (state, action: PayloadAction<EditMenuNodeSlice>) => {
    },
    reset: (state) => {
      return state.map(node => ({
        ...node,
        selected: false
      }))
    },
    toggleExpanded: (state, action: PayloadAction<EditMenuNodeSlice>) => {
      const node = state.find(node => node.id === action.payload.id);
      node.expanded = !node.expanded;
    },
    toggleSelected: (state, action: PayloadAction<EditMenuNodeSlice>) => {
      const id = action.payload.id.split('/');      
      const ctrlKey = action.payload.ctrlKey;
            
      // If no ctrl key selection starts from fresh i.e. no positions selected
      if (!ctrlKey) {
        state.forEach((node, index) => {
          state[index].selected = false;          
        });
      }

      if (id.length === 1) {
        
        // Find, change and record project state
        var project_state = null;
        state.forEach((node, index) => {          
          if (state[index].id === id[0]) { 
            state[index].selected = !state[index].selected;
            project_state = state[index].selected;
          };
        });
        
        // Find, change and record site state
        var site_ids = [];
        state.forEach((node, index) => {
          if (state[index].parent_id === id[0]) { 
            state[index].selected = project_state;
            site_ids.push(state[index].id);
          };
        });
        
        // Change position state
        state.forEach((node, index) => {
          if (site_ids.includes(state[index].parent_id)) { 
            state[index].selected = project_state;
          };
        });
        
      } else if (id.length === 2) {
    
        // Find, change and record site state
        var parent_state = null;
        state.forEach((node, index) => {
          if (state[index].id === id[1]) { 
            state[index].selected = !state[index].selected;
            parent_state = state[index].selected;
          };
        });
        
        state.forEach((node, index) => {
          if (state[index].parent_id === id[1]) {
            state[index].selected = parent_state;
          }
        });
        
      } else if (id.length === 3) {
        
        state.forEach((node, index) => {
          if (state[index].id === id[2]) {
            state[index].selected = !state[index].selected;
          }
        });
        
      }
    },
    setUsePowerSchedules: (state, action: PayloadAction<{id: string; features: any;}>) => {
      const node = state.find(node => node.id === action.payload.id)
      node.features.use_power_schedules = action.payload.features.use_power_schedules;;
    },
    setUseRawaudioSchedules: (state, action: PayloadAction<{id: string; features: any;}>) => {
      const node = state.find(node => node.id === action.payload.id)
      node.features.use_rawaudio_schedules = action.payload.features.use_rawaudio_schedules;;
    },
    setSelectedNodes: (state, action: PayloadAction<{ids: string[]; }>) => {
      
      // Reset all states
      state.forEach((node, index) => {
        state[index].selected = false;
      });
      
      action.payload.ids.forEach((id) => {
        // Handle selected site
        if (id.includes('s-')) {
          state.forEach((node, index) => {
            if (state[index].id === id) { 
              state[index].selected = true;
            }
            if (state[index].parent_id === id) {
              state[index].selected = true;
            }
          });
        }
      });
      
    },
    editNodeRebootDetail: (state, action: PayloadAction<EditRebootDetail>) => {
      // Handles the state of reboot details of a node
      const node = state.find(node => node.id === action.payload.id);
      
      if (notUndefined(action.payload.byweekday)) node.byweekday = action.payload.byweekday;
      if (notUndefined(action.payload.dtstart)) node.dtstart = action.payload.dtstart;

      node.saved = false;
      if (notUndefined(action.payload.saved)) {
        node.saved = action.payload.saved;
      }      
    },
    focusNodeRebootDetail: (state, action: PayloadAction<EditRebootDetail>) => {
      
      state.forEach((schedule, index) => {
        if (state[index].focus === true) {
          state[index].focus = false;
        }
        if (state[index].id === action.payload.id) {
          state[index].focus = true;
        }
      });

    }
  }
});

export const { toggleExpanded, toggleSelected, loadMenuNodes, getNodesFailed, setUsePowerSchedules, setUseRawaudioSchedules, setSelectedNodes, editNodeRebootDetail, focusNodeRebootDetail } = menuNodesSlice.actions;

export const getSelectedNodes = (state: RootState) => {
  return state.menuNodes.filter((node) => node.selected)
};

export const selectAllNodePositions = (state: RootState) => {
  return state.menuNodes.filter(node => node.type === 'station')
};

export const selectAllNodeSites = (state: RootState) => {
  return state.menuNodes.filter(node => node.type === 'site')
};

export const selectAllOnlineNodePositions = (state: RootState) => {
  return state.menuNodes.filter(node => node.type === 'station' && node.status && 
    (node.status.online_status === "online" || 
      node.status.online_status === "unbuffering" || 
      node.status.online_status === "delayed" ||
      node.status.online_status === "power_saving" ||
      node.status.online_status === "shortOffline"
    )
  );
};

export const selectAllOfflineNodePositions = (state: RootState) => {
  return state.menuNodes.filter(node => node.type === 'station' && (node.status.online_status === 'offline' || node.status.online_status === 'not_measuring'))
};

export const selectAllSelectedNodeSites = (state: RootState) => {
  return state.menuNodes.filter(node => node.type === 'site' && node.selected)
};

export const selectAllNodesGraphed = (state: RootState) => {    
  return unflatten(cloneDeep(state.menuNodes))
}

export const selectAllNodesSitesGraphed = (state: RootState) => {    
  const unflattened = unflatten(cloneDeep(state.menuNodes));
  const sites = unflattened.map(menuNode => menuNode.nodes).flat();
  return sites;
}

export const selectAllOnlineNodeSites = (state: RootState) => {    
  const unflattened = unflatten(cloneDeep(state.menuNodes));
  const sites = unflattened.map(menuNode => menuNode.nodes).flat();

  // Check if a node has any station with online status
  const siteHasOnlinePosition = (nodes) => {
    return nodes.some(station => 
      station.status && station.status.online_status === "online"
    );
  };

  // return filtered sites array as site, where the site has nodes and check that at least one node is online
  return sites.filter(site => site.nodes && siteHasOnlinePosition(site.nodes));
};

export const selectAllOfflineNodeSites = (state: RootState) => {
  const unflattened = unflatten(cloneDeep(state.menuNodes));
  const sites = unflattened.map(menuNode => menuNode.nodes).flat();

  // Check if nodes have offline status
  const siteHasOfflinePosition = (nodes) => {
    return nodes.some(station =>
      station.status && station.status.online_status === "offline"
    );
  };

  // return filtered sites array as site, where the site has nodes and check that at least one node is offline
  return sites.filter(site => site.nodes && siteHasOfflinePosition(site.nodes));
}


const unflatten = function(array, parent?, tree?) {
  /**
   * This function creates a nested array of objects,
   * depending whether its a project, site or station by using its id
   */
  tree = notUndefined(tree) ? tree : [];
  parent = notUndefined(parent) ? parent : { id: 0 };

  var children = array.filter((child) => {
    return child.parent_id == parent.id
  })

  if (children.length > 0) {
    if (parent.id === 0) {
      tree = children;
    } else {
      parent['nodes'] = children
    }
    children.forEach((child) => {
      unflatten(array, child);
    })
  } 

  return tree;
}

const prepareDatetime = function converTimeToFormat(reboot_time=null){
  /**
   * This function receives a time parameter or a null. 
   * If the received parameter is not a null, it should be changed to a datetime parameter 
   * to display on react-big-calendar on weekly-restart tab. Therefore, this function converts 
   * the time to a datetime parameter. This date part will have no impact when posting values 
   * to the database. For the sake of compatibilty, this input variable will be converted to a 
   * datetime parameter.
   */

  if (reboot_time == null) return reboot_time; 

  const date = new Date();  
  date.setDate(date.getDate()-7);  
  reboot_time =  date.toISOString().slice(0, 10) + ' ' + reboot_time;
  return reboot_time;   
}

const prepareWeekday = function convertWeekdaysToInts(reboot_day=null){
  /**
    * This function translates the weekday into an integer if it is not a null. 
    * It matches the variable to the lookup array and returns the index value 
    */

  if (reboot_day == null) return reboot_day;

  const lookup = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const weekday = [];  
  weekday.push(lookup.indexOf(reboot_day));  
  return weekday;
}

const prepareTabTypeColor = function capabilitycheck(icon_type, type, instrument_type, instrument_subtype, co_located_id, capability=null){
  
  // This function provides the icon color for each category. i.e: power, rawaudio, reboot
   
  const power = 'power';
  const rawaudio = 'rawaudio';
  const reboot = 'reboot';  
  const red = 'red';
  const amber = 'amber'

  // If not a station, no checks are required for the node
  if (type !== 'station') return null;

  // Identify any red tabs before proceeding
  switch(icon_type){

    // For Power Saving and reboot
    case power:
    case reboot:

      // Vibra units are not capable of power saving or weekly reboot
      if(instrument_type === instrument_types.vibration && instrument_subtype === instrument_sub_types.vibra ) return red;

      // If station is co-located and met, return red (as managed by parent station)
      if(notNull(co_located_id) && instrument_type === instrument_types.met) return red;

      break;

    // For NX-42WR Audio Recording
    case rawaudio:

      // If instrument is not noise, return red (as unit not capable of audio recording)
      if(instrument_type !== instrument_types.noise) return red;

      break;
  }

  // Return amber if capability attribute is null
  if(isNull(capability)) return amber

  // Otherwise, good to go for this category
  return null;
}

const prepareNodes = function(dispatch, nodes) {
  
  /**
   * Irrespective of the type (Ex; station, site, project) this function creates an array of objects 
   * named 'struct_nodes[]' having the same structure.
   * It creates three temporary array of objects to stations, sites and projects and finally it will merge all three arrays into one (struct_nodes)
   */  
  let monitors = [];
  let stations = [];
  let sites = [];
  let projects = [];
  let struct_nodes = [];

  Object.keys(nodes).forEach( key => {  

    const node_data = nodes[key]['data'];  

    if (node_data.length !== 0){     
      
      // Checks if the same station is being added to the array
      const station_exist = stations.filter((station) => (station.id === node_data.id));  

      if (!station_exist.length){
        
        const type = 'station';
        const instrument_type = node_data.type_id;
        const instrument_sub_type = node_data.subtype_id;
        const co_located_id = node_data.monitor.co_located_id;

        let temp_features = {
          use_power_schedules: node_data.use_power_schedules,
          use_rawaudio_schedules: node_data.use_rawaudio_schedules
        }

        let temp_stations = {
          label: node_data.title,
          id: node_data.id.toString(),
          monitor_id: node_data.monitor.id,
          type: type,
          instrument_type:  instrument_type,
          instrument_subtype: instrument_sub_type,
          parent_id: 's-' + node_data.site_id,
          features: temp_features,
          co_located_id: co_located_id,
          byweekday: prepareWeekday(node_data.reboot_day),
          dtstart: prepareDatetime(node_data.reboot_time),
          style: {background: '#66CC6A', border: '#49924C'},
          saved: true,
          status: node_data.status,
          weekly_reboot_enabled: node_data.weekly_reboot_enabled,
          icons: {
            power: prepareTabTypeColor('power', type, instrument_type, instrument_sub_type, co_located_id, temp_features.use_power_schedules),
            rawaudio: prepareTabTypeColor('rawaudio', type, instrument_type, instrument_sub_type, co_located_id, temp_features.use_rawaudio_schedules),
            reboot: prepareTabTypeColor('reboot', type, instrument_type, instrument_sub_type, co_located_id, node_data.weekly_reboot_enabled)
          },
          monitor_serial: node_data.monitor.serial,
        }         
        stations.push(temp_stations);         
      }
      
      // Checks if the instrument type is not a co_located weather station
      if (!(node_data.type_id == instrument_types.met && notNull(node_data.monitor.co_located_id))){

        // Checks if the same station is being added to the array
        const monitor_exist = monitors.filter((monitor) => (monitor.id === node_data.monitor.id)); 

        // Checks if monitor doest not exist containing monitor details
        if (!monitor_exist.length){

          // Append temporary monitor to the array monitors
          monitors.push(node_data.monitor)          
        }
      }
              
      // Checks if the same site is being added to the array
      const site_exist = sites.filter(site => site.id === ('s-'+node_data.site.id));  

      if (!site_exist.length){
        let temp_sites ={
          label: node_data.site.title,
          id: 's-' + node_data.site.id,
          type: 'site',            
          parent_id: 'p-' + node_data.site.project_id,
          
        }
        sites.push(temp_sites);         
      }       
      // Checks if the same project is being added to the array
      const project_exist = projects.filter(project => project.id === ('p-'+node_data.site.project_id));

      if (!project_exist.length){
        let temp_projects ={
          label: node_data.site.project.title,
          id: 'p-' + node_data.site.project_id,
          type: 'project',            
          parent_id: '0',
          
        }
        projects.push(temp_projects);         
      }  
    }      
    
  });
  
  try{
    dispatch(loadMonitor(monitors))
  } catch (err) {
    dispatch(getNodesFailed(err.toString()))
  }
  
  
  return struct_nodes.concat(stations, sites, projects)
}

export const fetchNodes = (): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  try {
    const nodes = await getNodes(user.api_key, user.account.id);
    const struct_nodes = prepareNodes(dispatch, nodes);
    
    const modified_nodes = struct_nodes.map(node => {
      let selected = false;
      let expanded = true;
      if (node.type === 'station') {
        expanded = null;
      } else if (node.type === 'site') {
        expanded = false;
      } else {
        expanded = true;
      }
      
      return {
        ...node,
        selected: selected,
        expanded: expanded
      }
    })
    
    dispatch(loadMenuNodes(modified_nodes))
    
  } catch (err) {
    dispatch(getNodesFailed(err.toString()))
  }
};

export const initAllNodes = (): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  console.log('getting multiple nodes')
  
  try {
    const nodes = await getNodes(user.api_key, user.account.id);
    const struct_nodes = prepareNodes(dispatch, nodes);
    let project = null;
    
    const modified_nodes = struct_nodes.map(node => {
      let selected = true;
      let expanded = true;
      
      return {
        ...node,
        selected: selected,
        expanded: expanded
      }
    })
    batch(() => {
      dispatch(loadMenuNodes(modified_nodes));
    })
    
  } catch (err) {
    dispatch(getNodesFailed(err.toString()))
  }
};

export const initNodes = (): AppThunk => async (dispatch, getState) => {
  const { user } = getState();
  console.log('getting multiple nodes')
  
  try {
    const nodes = await getNodes(user.api_key, user.account.id);
    const struct_nodes = prepareNodes(dispatch, nodes);
    let project = null;
    
    const modified_nodes = struct_nodes.map(node => {
      let selected = false;
      let expanded = true;
      if (node.type === 'station') {
        expanded = null;
      } else if (node.type === 'site') {
        expanded = false;
      } else {
        expanded = true;
      }
      
      if (project === null && node.type === 'project') {
        project = node.id;
      }
      
      return {
        ...node,
        selected: selected,
        expanded: expanded
      }
    })
    batch(() => {
      dispatch(loadMenuNodes(modified_nodes));
      dispatch(toggleSelected({ id: project }))
    })
    
  } catch (err) {
    dispatch(getNodesFailed(err.toString()))
  }
};

export const initSingleNode = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const user = state.user;  

  console.log('getting single node');   
  try {

    const nodes = await getNodes(user.api_key, user.account.id);
    const struct_nodes = prepareNodes(dispatch, nodes);

    let station = null;
    
    const modified_nodes = struct_nodes.map(node => {
      let selected = false;
      let expanded = false;
      if (node.type === 'station') {
        expanded = null;
      } else if (node.type === 'site') {
        expanded = false;
      } else {
        expanded = false;
      }
      
      if (station === null && node.type === 'station') {
        station = node.id;
      }
      
      return {
        ...node,
        selected: selected,
        expanded: expanded
      }
    })

    batch(() => {
      dispatch(loadMenuNodes(modified_nodes));
    })
    
  } catch (err) {
    dispatch(getNodesFailed(err.toString()))
  }
};

export const toggleUsePowerSchedules = (selectedNodes: Array<MenuNodeSlice>): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  try {
    const new_features = cloneDeep(selectedNodes[0].features);
    new_features.use_power_schedules = !new_features.use_power_schedules;
    const position = await setFeatures(user.api_key, selectedNodes[0].id, new_features)
    
    dispatch(setUsePowerSchedules({id: selectedNodes[0].id, features: new_features}))
  } catch (err) {
    dispatch(setTemporaryMessage('Failed to ' + err.toString()))
  }
};

export const updateNodeToDb = (node: EditRebootDetail): AppThunk => async (dispatch, getState) => {
  const { user } = getState();
  /**
   * Refering the API call to update reboot values for a given station.
   * reboot_day, should be an integer between 1 to 7 or string format as follows
   * ['MON', 'TUE, 'WED', 'THU', 'FRI', 'SAT', 'SUN'] 
   * reboot_time, should be either in 'HH:mm:ss' or 'HH:mm
   */

  try {  
    var reboot_time = moment(node.dtstart);  
    const result = await putNodeRebootDetails(user.api_key, {
      id: node.id,      
      reboot_day: node.byweekday[0] + 1,
      reboot_time: reboot_time.format("HH:mm"),  
    });

    dispatch(editNodeRebootDetail({id: node.id, saved: true, editable: true}));

  } catch (err) {
    dispatch(setTemporaryMessage('Failed to update'));
  }

}

export default menuNodesSlice.reducer;
